import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { PEV } from "src/common/utils";
import { ProgressErrorValue } from "src/common/utils/progress-error-value";
import * as service from "../service/information-asset-accesses";
import { AppliedScope, CurrentAppliedScopesWithUsersMap, GroupAppliedScopesByUsersCount } from "./current-applied-scopes";
import { InformationAssetUser } from "./information-asset-user";
import { Scope } from "./system-resources";
import { SystemUserGroup } from "./system-user-group";

export const SYSTEM_USER_GROUP = "SystemUserGroup";
export const SYSTEM_USER = "SystemUser";

export class SystemResourceUniqueIdentifier {
  systemResourceId: string;
  systemId: string;

  constructor(systemResource: SystemResourceUniqueIdentifier) {
    this.systemResourceId = systemResource.systemResourceId;
    this.systemId = systemResource.systemId;
  }
}

export type IndividualUsersUniquePrincipalIdentifier = {
  type: string;
  email: string;
};

export enum PrincipalType {
  SystemUser = "SystemUser",
  SystemServiceAccount = "SystemServiceAccount",
  GoogleGroup = "GoogleGroup",
  SystemUserGroup = "SystemUserGroup",
}

export class Principal {
  id: string;
  type: PrincipalType;
  email: string;

  constructor(principal: Principal) {
    this.type = principal.type;
    this.id = principal.id;
    this.email = principal.email;
  }

  isIndividualUser() {
    return this.type !== PrincipalType.SystemUserGroup;
  }

  isGroup() {
    return this.type === PrincipalType.SystemUserGroup;
  }

  individualUsersUniquePrincipalIdentifier(): IndividualUsersUniquePrincipalIdentifier {
    return { type: this.type, email: this.email };
  }
}

export class Action {
  name: string;
  scopes: Scope[] | null;

  constructor(action: Action) {
    this.name = action.name;
    this.scopes = action.scopes?.map((scope) => new Scope(scope)) ?? null;
  }
}

export enum ObtainedViaType {
  DirectAccess = "DIRECT_ACCESS",
  GroupDefault = "GROUP_DEFAULT",
  GroupOverride = "GROUP_OVERRIDE",
}

export class ObtainedVia {
  type: ObtainedViaType;
  groupId: string | null;

  constructor(obtainedVia: ObtainedVia) {
    this.type = obtainedVia.type;
    this.groupId = obtainedVia.groupId;
  }

  isDirectAccess(): boolean {
    return this.type === ObtainedViaType.DirectAccess;
  }
}

export class Permission {
  actionName: string;
  scope: Scope | null;
  obtainedVia: ObtainedVia;

  constructor(permission: Permission) {
    this.actionName = permission.actionName;
    this.scope = permission.scope ? new Scope(permission.scope) : null;
    this.obtainedVia = new ObtainedVia(permission.obtainedVia);
  }

  isDirectAccess(): boolean {
    return this.obtainedVia.isDirectAccess();
  }

  isAssociatedToGroup(systemUserGroupId: string): boolean {
    return this.obtainedVia.groupId === systemUserGroupId;
  }

  appliedScope(): AppliedScope {
    return new AppliedScope(this.actionName, this.scope ? this.scope.name : "");
  }
}

export class Access {
  principal: Principal;
  permissions: Permission[];

  constructor(access: Access) {
    this.principal = new Principal(access.principal);
    this.permissions = access.permissions.map((permission) => new Permission(permission));
  }

  hasDirectAccess(): boolean {
    return this.permissions.some((permission) => permission.isDirectAccess());
  }

  appliedScopes(): Set<string> {
    return new Set(this.permissions.map((permission) => permission.appliedScope().stringFormat()));
  }

  appliedScopesWithUsersForSystemUserGroup(
    systemUserGroupId: string
  ): CurrentAppliedScopesWithUsersMap {
    const appliedScopesWithUsers: CurrentAppliedScopesWithUsersMap =
      new CurrentAppliedScopesWithUsersMap();
    this.filterPermissionsBasedOnSystemUserGroup(systemUserGroupId).forEach((permission) => {
      if (this.principal.isGroup()) {
        appliedScopesWithUsers.addScopeIfNotPresent(permission.appliedScope());
      } else {
        appliedScopesWithUsers.addUserToScope(
          permission.appliedScope(),
          this.principal.individualUsersUniquePrincipalIdentifier()
        );
      }
    });
    return appliedScopesWithUsers;
  }

  filterPermissionsBasedOnSystemUserGroup(systemUserGroupId: string) {
    return this.permissions.filter(
      (permission) =>
        permission.isAssociatedToGroup(systemUserGroupId) ||
        (this.principal.isGroup() && this.principal.id === systemUserGroupId)
    );
  }

  hasPermissionsToUserGroup(systemUserGroupId: string): boolean {
    return this.permissions.some((permission) => permission.isAssociatedToGroup(systemUserGroupId));
  }

  hasAccessToUserGroup(systemUserGroupId: string): boolean {
    return this.principal.isGroup() && this.principal.id === systemUserGroupId;
  }

  otherAppliedScopesForUsersInSystemUserGroup(
    systemUserGroupId: string
  ): CurrentAppliedScopesWithUsersMap {
    const appliedScopesWithUsers: CurrentAppliedScopesWithUsersMap =
      new CurrentAppliedScopesWithUsersMap();
    if (this.hasPermissionsToUserGroup(systemUserGroupId)) {
      this.permissions
        .filter((permission) => !permission.isAssociatedToGroup(systemUserGroupId))
        .forEach((permission) =>
          appliedScopesWithUsers.addUserToScope(
            permission.appliedScope(),
            this.principal.individualUsersUniquePrincipalIdentifier()
          )
        );
    }
    return appliedScopesWithUsers;
  }
}

export class ResourceAccess {
  resourceId: string;
  accesses: Access[];

  constructor(resourceAccess: ResourceAccess) {
    this.resourceId = resourceAccess.resourceId;
    this.accesses = resourceAccess.accesses?.map((access) => new Access(access));
  }

  individualUsersAccesses(): Access[] {
    return this.accesses.filter((access) => access.principal.isIndividualUser());
  }

  groups() {
    return this.accesses.filter((access) => access.principal.isGroup());
  }

  appliedScopes(): Set<string> {
    const appliedScopes: Set<string> = new Set();
    this.accesses.forEach((access) =>
      access.appliedScopes().forEach((scope) => appliedScopes.add(scope))
    );
    return appliedScopes;
  }

  isResourceAccessAssociatedToResource(resourceId: string) {
    return this.resourceId === resourceId;
  }

  isIndividualUsersIncludeUser(email: string) {
    return this.individualUsersAccesses()
      .some((access) => access.principal.email === email)
      .valueOf();
  }

  isGroupsIncludeUser(systemUserGroups: SystemUserGroup[], email: string, systemId: string) {
    return this.groups().some((access) => {
      const foundSystemUserGroup = systemUserGroups.find(
        (systemUserGroup) =>
          systemUserGroup.systemUserGroupId === access.principal.id &&
          systemUserGroup.systemId === systemId
      );
      return foundSystemUserGroup?.members.some((member) => member.email === email).valueOf();
    });
  }

  appliedScopesWithUsersForUserGroup(systemUserGroupId: string): CurrentAppliedScopesWithUsersMap {
    let appliedScopesWithUsers: CurrentAppliedScopesWithUsersMap =
      new CurrentAppliedScopesWithUsersMap();
    this.accesses.forEach(
      (access) =>
        (appliedScopesWithUsers = appliedScopesWithUsers.combine(
          access.appliedScopesWithUsersForSystemUserGroup(systemUserGroupId)
        ))
    );
    return appliedScopesWithUsers;
  }

  otherAppliedScopesForUsersInSystemUserGroup(
    systemUserGroupId: string
  ): CurrentAppliedScopesWithUsersMap {
    let appliedScopesWithUsers: CurrentAppliedScopesWithUsersMap =
      new CurrentAppliedScopesWithUsersMap();
    this.accesses.forEach(
      (access) =>
        (appliedScopesWithUsers = appliedScopesWithUsers.combine(
          access.otherAppliedScopesForUsersInSystemUserGroup(systemUserGroupId)
        ))
    );
    return appliedScopesWithUsers;
  }

  appliedScopesForUserGroupDetails(userEmail: string): Set<string> {
    const actionScopes = new Set<string>();
    this.accesses
      .filter((access) => this.filterMatchedAccesses(access, userEmail))
      .forEach((access) =>
        access.permissions.forEach((permission) =>
          actionScopes.add(permission.appliedScope().stringFormat())
        )
      );
    return actionScopes;
  }

  filterMatchedAccesses(access: Access, userEmail: string) {
    return access.principal.email === userEmail;
  }

  hasAccessToUserGroup(systemUserGroupId: string) {
    return this.accesses.some((access) => access.hasAccessToUserGroup(systemUserGroupId));
  }
}

export class CurrentSystemResourceAccess {
  systemResource: SystemResourceUniqueIdentifier;
  resourceAccesses: ResourceAccess[];

  constructor(currentAccessesForSystemResource: CurrentSystemResourceAccess) {
    this.systemResource = new SystemResourceUniqueIdentifier(
      currentAccessesForSystemResource.systemResource
    );
    this.resourceAccesses = currentAccessesForSystemResource.resourceAccesses?.map(
      (resourceAccess) => new ResourceAccess(resourceAccess)
    );
  }

  getResourceAccessesAssociatedToResource(resourceId: string) {
    return this.resourceAccesses.filter((resourceAccess) =>
      resourceAccess.isResourceAccessAssociatedToResource(resourceId)
    );
  }

  resourceAccess(resourceId: String): ResourceAccess | undefined {
    return this.resourceAccesses.find((resourceAccess) => resourceAccess.resourceId === resourceId);
  }

  isCurrentSystemResourceAccessAssociatedToResource(resourceId: string) {
    return this.resourceAccesses.some((resourceAccess) =>
      resourceAccess.isResourceAccessAssociatedToResource(resourceId)
    );
  }

  hasAccessToSystemUserGroup(systemUserGroupId: string, resourceId: string): boolean {
    return this.resourceAccesses.some(
      (resourceAccess) =>
        resourceAccess.isResourceAccessAssociatedToResource(resourceId) &&
        resourceAccess.hasAccessToUserGroup(systemUserGroupId)
    );
  }
}

export class CurrentSystemResourceAccesses {
  currentAccesses: CurrentSystemResourceAccess[];

  constructor(currentAccessesForSystemResources: CurrentSystemResourceAccess[]) {
    this.currentAccesses = currentAccessesForSystemResources.map(
      (currentAccessesForSystemResource) =>
        new CurrentSystemResourceAccess(currentAccessesForSystemResource)
    );
  }

  fetchResourceAccesses(
    systemResourceId: String,
    systemId: String,
    resourceId: String
  ): ResourceAccess | undefined {
    const systemResourceAccess = this.currentAccesses.find(
      (currentAccessesForSystemResource) =>
        currentAccessesForSystemResource.systemResource.systemResourceId === systemResourceId &&
        currentAccessesForSystemResource.systemResource.systemId === systemId
    );
    return systemResourceAccess?.resourceAccesses.find(
      (resourceAccess) => resourceAccess.resourceId === resourceId
    );
  }

  fetchSystemResourceAccessesForUser(
    user: InformationAssetUser,
    systemUserGroups: SystemUserGroup[],
    systemId: string,
    resourceId: string
  ) {
    return this.currentAccesses?.filter((currentAccess) =>
      currentAccess.resourceAccesses.some(
        (resourceAccess) =>
          resourceAccess.resourceId === resourceId &&
          (resourceAccess.isIndividualUsersIncludeUser(user.email) ||
            resourceAccess.isGroupsIncludeUser(systemUserGroups, user.email, systemId))
      )
    );
  }

  individualUsersAdded(systemResourceId: String, systemId: String, assetId: String): String[] {
    return (
      this.fetchResourceAccesses(systemResourceId, systemId, assetId)
        ?.individualUsersAccesses()
        .filter((individualUsersAccess) => individualUsersAccess.hasDirectAccess())
        .map((individualUser) => individualUser.principal.email) ?? []
    );
  }

  currentAppliedScopes(
    systemResourceId: String,
    systemId: String,
    assetId: String
  ): AppliedScope[] {
    const appliedScopes: AppliedScope[] = [];
    this.fetchResourceAccesses(systemResourceId, systemId, assetId)
      ?.appliedScopes()
      .forEach((appliedScope) => {
        appliedScopes.push(new AppliedScope(JSON.parse(appliedScope)));
      });
    return appliedScopes;
  }

  groups(systemResourceId: String, systemId: String, assetId: String): String[] {
    return (
      this.fetchResourceAccesses(systemResourceId, systemId, assetId)
        ?.groups()
        .map((individualUser) => individualUser.principal.id) ?? []
    );
  }

  currentAppliedScopesWithUsersForSystemUserGroup(
    systemId: string,
    systemUserGroupId: string,
    resourceId: string
  ): CurrentAppliedScopesWithUsersMap {
    let appliedScopesWithUsers: CurrentAppliedScopesWithUsersMap =
      new CurrentAppliedScopesWithUsersMap();
    this.fetchSystemResourcesBasedOnSystemId(systemId).forEach(
      (currentAccess) =>
        (appliedScopesWithUsers = appliedScopesWithUsers.combine(
          currentAccess
            .resourceAccess(resourceId)
            ?.appliedScopesWithUsersForUserGroup(systemUserGroupId) ??
            new CurrentAppliedScopesWithUsersMap()
        ))
    );
    return appliedScopesWithUsers;
  }

  fetchSystemResourcesThatHasAccessToUserGroup(
    systemId: string,
    systemUserGroupId: string,
    resourceId: string
  ): CurrentSystemResourceAccess[] {
    return this.currentAccesses.filter(
      (currentAccess) =>
        currentAccess.systemResource.systemId === systemId &&
        currentAccess.hasAccessToSystemUserGroup(systemUserGroupId, resourceId)
    );
  }

  otherAppliedScopesForUsersInSystemUserGroup(
    systemId: string,
    systemUserGroupId: string,
    resourceId: string
  ): CurrentAppliedScopesWithUsersMap {
    let appliedScopesWithUsers: CurrentAppliedScopesWithUsersMap =
      new CurrentAppliedScopesWithUsersMap();
    this.fetchSystemResourcesThatHasAccessToUserGroup(
      systemId,
      systemUserGroupId,
      resourceId
    ).forEach(
      (currentAccess) =>
        (appliedScopesWithUsers = appliedScopesWithUsers.combine(
          currentAccess
            .resourceAccess(resourceId)
            ?.otherAppliedScopesForUsersInSystemUserGroup(systemUserGroupId) ??
            new CurrentAppliedScopesWithUsersMap()
        ))
    );

    return appliedScopesWithUsers;
  }

  fetchSystemResourcesBasedOnSystemId(systemId: string) {
    return this.currentAccesses.filter(
      (currentAccess) => currentAccess.systemResource.systemId === systemId
    );
  }
  overAllScopesForTheSystemUserGroup(
    systemId: string,
    systemUserGroupId: string,
    resourceId: string
  ): GroupAppliedScopesByUsersCount{
    const aggregatedScopesWithUsers: CurrentAppliedScopesWithUsersMap = this
      .currentAppliedScopesWithUsersForSystemUserGroup(systemId, systemUserGroupId, resourceId)
      .combine(
        this.otherAppliedScopesForUsersInSystemUserGroup(
          systemId,
          systemUserGroupId,
          resourceId
        )
      );

    return aggregatedScopesWithUsers.groupScopesByUsersCount();

  }
}

export interface InformationAssetsAccesses {
  informationAssetsAccessesPEV: ProgressErrorValue<CurrentSystemResourceAccesses>;
}

const initialState: InformationAssetsAccesses = {
  // @ts-ignore
  informationAssetsAccessesPEV: PEV(null, "Not loaded yet"),
};

const fetchInformationAssetsAccesses = createAsyncThunk("information-accesses/fetch", async () => {
  return await service.fetchInformationAssetsAccesses();
});

const informationAssetsAccessesSlice = createSlice({
  name: "informationAssetsAccesses",
  initialState,
  reducers: {
    updateInformationAssetsAccessesState: (
      state: InformationAssetsAccesses,
      action: PayloadAction<Partial<InformationAssetsAccesses>>
    ) => ({
      ...state,
      ...action.payload,
    }),
    resetInformationAssetAccesses(state) {
      state.informationAssetsAccessesPEV = initialState.informationAssetsAccessesPEV;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchInformationAssetsAccesses.pending, (state) => {
        // @ts-ignore
        state.informationAssetsAccessesPEV = PEV(
          state.informationAssetsAccessesPEV.value, // @ts-ignore
          "Fetching information assets accesses"
        );
      })
      .addCase(fetchInformationAssetsAccesses.fulfilled, (state, action) => {
        state.informationAssetsAccessesPEV = PEV(action.payload);
      })
      .addCase(fetchInformationAssetsAccesses.rejected.type, (state, action) => {
        // @ts-ignore
        state.informationAssetsAccessesPEV = PEV(
          state.informationAssetsAccessesPEV.value,
          null, // @ts-ignore
          "Error while fetching assets accesses"
        );
      });
  },
});

const { resetInformationAssetAccesses } = informationAssetsAccessesSlice.actions;
export const InformationAssetsAccessesReducer = informationAssetsAccessesSlice.reducer;

export const InformationAssetsAccessesActions = {
  fetchInformationAssetsAccesses,
  resetInformationAssetAccesses,
};
